import { cva } from "class-variance-authority";
import { shapeClass, type Shape } from "../../lib/shape.js";
import { STATE_RING, STATE_RING_BY_TONE } from "../../lib/state-ring.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import { cn } from "../../lib/utils.js";

/* Button's class matrix, split out of Button.tsx so every Button ticket edits
   one file (C7). The API notes live on the component in Button.tsx.

   Size is the control ramp 24/28/36/44px (xs/sm/md/lg); md is the default.
   `inline` has no box: no height, no padding, and it takes its font size,
   line height and weight from the sentence around it. It is typed to
   `variant="link"` only, and link + inline is underlined.

   Tone (C7 names) is typed per variant: ButtonVariantTones lists the tones
   each variant takes, and a variant it does not list takes none. A toned
   pair REPLACES the variant's paint, so no untoned colour class is left for
   a merge to resolve. Rest-state tone classes (text, border, soft fill) come
   from lib/tones.ts; hover and active paints are the literal classes here.

   Shape (C7 words from lib/shape.ts): `rounded` is the default and today's
   8px corner. Its rounded-md stays in the base string, so `rounded` adds no
   class and the default string does not change. `pill` and `circle` add
   rounded-full, and cn() in Button.tsx drops the base rounded-md. `circle`
   is the iconOnly square box made fully round.

   Pressed (button-pressed-busy) keys off the `pressed` option, never an
   aria-pressed selector, so a caller that sets aria-pressed itself keeps
   today's look. Untoned, it paints the teal selection (C7: teal marks the
   selected state only), plus a teal border on secondary and outline. Toned,
   it paints that tone's soft fill, text and border from lib/tones.ts and no
   teal. Its classes merge over the variant paint, so they win. */

/** The tones each variant accepts. `secondary`, `destructive`, `inverse`,
 *  `dashed` and `plain` take none. `accent` is never a tone (invariant 9). */
export interface ButtonVariantTones {
  /** `ai` is the iris CTA and ONLY for the Player AI FAB and the Player AI
   *  launcher (ADR-0076). Every other commit action stays accent. */
  primary: "ai";
  /** The tone paints on hover only; at rest it is a plain ghost. */
  ghost: "critical" | "ai";
  outline: "critical";
  /** Omitted, a link is the accent text blue (`text-pb-accent-text`). */
  link: "neutral" | "ai";
  /** Omitted, soft stays today's accented secondary. */
  soft: StatusTone;
}

/* Each variant's paint with no tone: today's classes, unchanged. */
const VARIANT_PAINT = {
  primary:
    "bg-linear-to-b from-pb-accent-hover to-pb-accent text-pb-accent-foreground shadow-glow-accent-rest hover:from-pb-accent-hover hover:to-pb-accent-hover hover:shadow-glow-accent active:from-pb-accent-active active:to-pb-accent-active active:shadow-glow-accent-rest",
  secondary:
    "bg-pb-card-elevated pb-hairline text-pb-text border border-pb-control-border hover:pb-overlay-paint active:pb-overlay-paint-strong",
  /* Hover and press change only the border: a darker tint would drop the
     accent text below 4.5:1 on a dialog surface (Jira-blue amendment, d11). */
  soft: "bg-pb-accent-soft text-pb-accent-text border border-pb-accent/25 hover:border-pb-accent/40 active:border-pb-accent/55",
  ghost:
    "bg-transparent hover:pb-overlay-paint active:pb-overlay-paint-strong text-pb-text-secondary hover:text-pb-text",
  outline:
    "bg-transparent border border-pb-control-border hover:pb-overlay-paint active:pb-overlay-paint-strong text-pb-text",
  destructive:
    "bg-pb-red hover:bg-pb-red/90 active:bg-pb-red/80 text-pb-on-accent",
  link: "bg-transparent text-pb-accent-text hover:text-pb-accent-active px-0",
  /* An unfilled "add" affordance (Hoshin, Shell, VSM dashed add rows): the
     muted rest ink and border lift to full strength on hover, alongside the
     shared overlay paint. */
  dashed:
    "bg-transparent border border-dashed border-pb-control-border text-pb-text-muted hover:border-pb-border-strong hover:text-pb-text hover:pb-overlay-paint",
  /* Paints no fill of its own: text-current, so it takes the colour of
     whatever surface it sits on (sticky paper, a solid pill header). Its
     hover/press wash is currentColor rather than the fixed overlay paint,
     so it reads correctly on any ink colour. */
  plain: "bg-transparent text-current hover:bg-current/10 active:bg-current/15",
  /* The dark-on-light action. Not a primary: no accent and no glow, so the
     one-primary-per-region count (invariant 9) never sees it. */
  inverse: "bg-pb-text text-pb-page hover:bg-pb-text/90 active:bg-pb-text/80",
} as const;

export type ButtonVariant = keyof typeof VARIANT_PAINT;

/** The corners a Button takes. `rounded` (the default) is today's 8px corner.
 *  `pill` is fully round and the width follows the content. `circle` is fully
 *  round on the iconOnly square box: 24/28/36/44px at xs/sm/md/lg. */
export type ButtonShape = Extract<Shape, "rounded" | "pill" | "circle">;

export type ButtonTone = ButtonVariantTones[keyof ButtonVariantTones];

type TonedKey = {
  [V in keyof ButtonVariantTones]: `${V}.${ButtonVariantTones[V]}`;
}[keyof ButtonVariantTones];

/* Each allowed variant x tone pair. `satisfies` makes the table complete:
   a pair the type allows cannot be missing, and no other pair can exist. */
const TONED_PAINT = {
  "primary.ai": `${TONES.ai.solid} bg-linear-to-b from-pb-iris-interactive-hover to-pb-iris-interactive shadow-glow-iris hover:from-pb-iris-interactive-hover hover:to-pb-iris-interactive-hover active:from-pb-iris-interactive-active active:to-pb-iris-interactive-active`,
  "ghost.critical":
    "bg-transparent text-pb-text-secondary hover:bg-pb-red-soft hover:text-pb-red-text active:bg-pb-red/20",
  "ghost.ai":
    "bg-transparent text-pb-text-secondary hover:bg-pb-iris-soft hover:text-pb-iris-text active:bg-pb-iris/20",
  "outline.critical": `bg-transparent border ${TONES.critical.border} ${TONES.critical.text} hover:bg-pb-red-soft active:bg-pb-red/20`,
  /* Not TONES.neutral.text (text-pb-text-muted): a link is an action and
     keeps full-strength ink. */
  "link.neutral":
    "bg-transparent text-pb-text hover:text-pb-text-secondary px-0",
  "link.ai": `bg-transparent ${TONES.ai.text} hover:underline underline-offset-2 px-0`,
  "soft.neutral": `border ${TONES.neutral.soft} hover:pb-overlay-paint active:pb-overlay-paint-strong`,
  /* As the blue soft (d11): the tint stays and hover and press darken only the
     border. A darker tint dropped the tone text to 3.82:1 on a dialog (d12). */
  "soft.healthy": `border ${TONES.healthy.soft} hover:border-pb-green/60 active:border-pb-green/75`,
  "soft.watch": `border ${TONES.watch.soft} hover:border-pb-amber/60 active:border-pb-amber/75`,
  "soft.critical": `border ${TONES.critical.soft} hover:border-pb-red/60 active:border-pb-red/75`,
  "soft.info": `border ${TONES.info.soft} hover:border-pb-cyan/60 active:border-pb-cyan/75`,
  "soft.ai": `border ${TONES.ai.soft} hover:border-pb-iris/60 active:border-pb-iris/75`,
} as const satisfies Record<TonedKey, string>;

const buttonClasses = cva(
  "inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-[color,background-color,background-image,border-color,box-shadow,transform] motion-reduce:transition-none focus-ring press disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      variant: { ...VARIANT_PAINT, ...TONED_PAINT },
      size: {
        xs: "h-6 px-1.5 gap-1 text-dense-1",
        sm: "h-7 px-2 text-xs",
        md: "h-9 px-3 text-sm",
        lg: "h-11 px-4 text-sm",
        inline:
          "h-auto p-0 text-[length:inherit] leading-[inherit] font-[number:inherit] text-start align-baseline",
      },
      iconOnly: {
        true: "p-0 aspect-square",
        false: "",
      },
      shape: {
        /* rounded-md is in the base string, so the default adds nothing. */
        rounded: "",
        pill: shapeClass("pill"),
        circle: shapeClass("circle"),
      },
    },
    compoundVariants: [
      /* xs iconOnly is the 24x24 remove / clear / dismiss / step control:
         the WCAG 2.2 2.5.8 minimum target, never smaller. */
      { iconOnly: true, size: "xs", class: "w-6 h-6" },
      { iconOnly: true, size: "sm", class: "w-7 h-7" },
      { iconOnly: true, size: "md", class: "w-9 h-9" },
      { iconOnly: true, size: "lg", class: "w-11 h-11" },
      {
        variant: ["link", "link.neutral", "link.ai"],
        size: "inline",
        class: "underline underline-offset-2",
      },
    ],
    defaultVariants: {
      variant: "secondary",
      size: "md",
      iconOnly: false,
      shape: "rounded",
    },
  },
);

export type ButtonSize = "xs" | "sm" | "md" | "lg" | "inline";

export interface ButtonVariantsOptions {
  variant?: ButtonVariant | null | undefined;
  tone?: ButtonTone | null | undefined;
  size?: ButtonSize | null | undefined;
  iconOnly?: boolean | null | undefined;
  shape?: ButtonShape | null | undefined;
  /** The `pressed` prop: paints the pressed state. */
  pressed?: boolean | null | undefined;
  /** Inactive under focusableWhenDisabled: paints today's disabled dim. */
  focusableDisabled?: boolean | null | undefined;
}

/* The teal selection: the pressed paint of an untoned Button, with the
   high-contrast ring that carries the state where the tint is too faint (d15). */
const PRESSED_SELECTION = `bg-pb-teal-soft text-pb-teal-text ${STATE_RING.teal}`;
const PRESSED_SELECTION_BORDER = "border-pb-teal/50";

/* The base string's disabled:opacity-50 disabled:pointer-events-none as plain
   classes: aria-disabled does not match :disabled, and a focusable disabled
   Button still looks disabled. */
export const FOCUSABLE_DISABLED_DIM = "opacity-50 pointer-events-none";

function pressedPaint(
  variant: ButtonVariant | null | undefined,
  tone: ButtonTone | null | undefined,
): string {
  if (tone) return `${TONES[tone].soft} ${STATE_RING_BY_TONE[tone]}`;
  const v = variant ?? "secondary";
  return v === "secondary" || v === "outline"
    ? `${PRESSED_SELECTION} ${PRESSED_SELECTION_BORDER}`
    : PRESSED_SELECTION;
}

/* With no tone, shape, pressed or focusableDisabled the class string is
   today's, byte for byte. */
export function buttonVariants({
  variant,
  tone,
  size,
  iconOnly,
  shape,
  pressed,
  focusableDisabled,
}: ButtonVariantsOptions = {}): string {
  const toned = `${variant ?? "secondary"}.${tone}`;
  const classes = buttonClasses({
    variant: tone && toned in TONED_PAINT ? (toned as TonedKey) : variant,
    size,
    // A circle is always the square box: it never stretches to its content.
    iconOnly: shape === "circle" || iconOnly,
    shape,
  });
  if (!pressed && !focusableDisabled) return classes;
  return cn(
    classes,
    pressed && pressedPaint(variant, tone),
    focusableDisabled && FOCUSABLE_DISABLED_DIM,
  );
}
